'use client'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { SITE_MAX, SITE_PAD_X } from '@/lib/site-layout'
import { categoryAccent } from '@/lib/category-accent'

export type StripItemKind = 'category' | 'tag' | 'breaking'

export interface HeaderStripItem {
  id: string
  kind: StripItemKind
  label: string
  slug: string
  sort?: number
  color?: string | null
}

type Props = {
  items: HeaderStripItem[]
}

const SON_DAKIKA_ITEM: HeaderStripItem = {
  id: 'son-dakika',
  kind: 'breaking',
  label: 'Son Dakika',
  slug: 'son-dakika',
}

function itemHref(item: HeaderStripItem) {
  if (item.kind === 'breaking') return '/son-dakika'
  return item.kind === 'tag' ? `/haberleri/${item.slug}` : `/${item.slug}`
}

function activeItemIndex(pathname: string, items: HeaderStripItem[]) {
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0] === 'son-dakika') {
    return items.findIndex((i) => i.kind === 'breaking')
  }
  if ((parts[0] === 'haberleri' || parts[0] === 'etiket') && parts[1]) {
    const slug = parts[1]
    return items.findIndex((i) => i.kind === 'tag' && i.slug === slug)
  }
  const seg = parts[0]
  if (!seg) return -1
  return items.findIndex((i) => i.kind === 'category' && i.slug === seg)
}

function StripPill({
  item,
  active,
}: {
  item: HeaderStripItem
  active: boolean
}) {
  const isTag = item.kind === 'tag'
  const isBreaking = item.kind === 'breaking'
  const isCategory = item.kind === 'category'
  const accent = isCategory ? categoryAccent(item.color) : categoryAccent(null)

  if (isBreaking) {
    return (
      <Link
        href="/son-dakika"
        className={cn(
          'header-son-dakika inline-flex shrink-0 touch-manipulation items-center rounded-full border px-2.5 py-1 text-[11px] font-extrabold uppercase leading-none tracking-wide transition-all duration-150 sm:px-3 sm:py-1.5 sm:text-xs',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D92E2E]/60 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900',
          active
            ? 'border-[#D92E2E] bg-[#D92E2E] !text-white shadow-sm'
            : 'border-[#D92E2E]/35 bg-white text-[#D92E2E] hover:border-[#D92E2E] hover:bg-red-50 dark:border-red-800/60 dark:bg-slate-800 dark:text-red-400 dark:hover:bg-red-950/40',
        )}
        aria-current={active ? 'page' : undefined}
      >
        Son Dakika
      </Link>
    )
  }

  return (
    <Link
      href={itemHref(item)}
      className={cn(
        'inline-flex max-w-[10rem] shrink-0 items-center gap-0.5 overflow-hidden rounded-full border px-2.5 py-1 text-xs font-semibold leading-none transition-all duration-150 sm:max-w-[14rem] sm:px-3 sm:py-1.5',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D92E2E]/60 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900',
        isTag
          ? active
            ? 'border-[#D92E2E]/40 bg-[#D92E2E] text-white shadow-sm'
            : 'border-red-200/80 bg-red-50/90 text-[#B91C1C] hover:border-[#D92E2E]/35 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/50 dark:text-red-300 dark:hover:bg-red-950/80'
          : active
            ? 'border bg-white shadow-sm dark:bg-slate-800'
            : 'border-gray-200/90 bg-white/95 text-gray-700 hover:border-gray-300 dark:border-slate-600 dark:bg-slate-800/90 dark:text-gray-200 dark:hover:border-slate-500',
        !isTag && !active && 'hover:opacity-90',
      )}
      style={
        isTag
          ? undefined
          : active
            ? { borderColor: `${accent}66`, color: accent }
            : undefined
      }
      onMouseEnter={
        isTag || active
          ? undefined
          : (e) => {
              e.currentTarget.style.color = accent
              e.currentTarget.style.borderColor = `${accent}55`
            }
      }
      onMouseLeave={
        isTag || active
          ? undefined
          : (e) => {
              e.currentTarget.style.color = ''
              e.currentTarget.style.borderColor = ''
            }
      }
      aria-current={active ? 'page' : undefined}
    >
      {isTag && (
        <span
          className={cn(
            'shrink-0 text-[11px] font-bold sm:text-xs',
            active ? 'text-white/90' : 'text-[#D92E2E]/80 dark:text-red-400/90',
          )}
          aria-hidden
        >
          #
        </span>
      )}
      <span className="truncate">{item.label}</span>
    </Link>
  )
}

export default function HeaderBottomStrip({ items }: Props) {
  const pathname = usePathname()
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const lastScrollY = useRef(0)
  const [visible, setVisible] = useState(true)
  /** Route değişiminde şerit animasyonsuz gelsin — tüm sayfalarda ikinci zıplama hissi olmasın */
  const [skipStripMotion, setSkipStripMotion] = useState(false)

  useLayoutEffect(() => {
    setVisible(true)
    setSkipStripMotion(true)
    lastScrollY.current = typeof window !== 'undefined' ? window.scrollY : 0
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSkipStripMotion(false))
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [pathname])

  useEffect(() => {
    lastScrollY.current = window.scrollY

    const onScroll = () => {
      const currentY = window.scrollY
      const delta = currentY - lastScrollY.current

      if (currentY <= 12) {
        setVisible(true)
      } else if (delta > 6) {
        setVisible(false)
      } else if (delta < -6) {
        setVisible(true)
      }

      lastScrollY.current = currentY
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const stripItems = useMemo(() => {
    const rest = items.filter(
      (i) => i.slug && i.label && i.slug !== 'son-dakika' && i.kind !== 'breaking',
    )
    return [SON_DAKIKA_ITEM, ...rest]
  }, [items])

  const activeIdx = useMemo(
    () => activeItemIndex(pathname, stripItems),
    [pathname, stripItems],
  )

  useEffect(() => {
    if (activeIdx < 0) return
    const root = scrollerRef.current
    if (!root) return
    const el = root.children[activeIdx] as HTMLElement | undefined
    el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  }, [activeIdx, pathname])

  return (
    <div
      data-header-bottom-strip
      className={cn(
        'fixed left-0 right-0 top-14 z-40 w-full min-w-0 max-w-[100vw] overflow-hidden will-change-transform md:top-[4.125rem]',
        skipStripMotion
          ? 'transition-none'
          : 'transition-transform duration-300 ease-out',
        visible ? 'translate-y-0' : '-translate-y-full',
      )}
    >
      <div className="w-full min-w-0 overflow-hidden border-b border-gray-200/80 bg-gray-50/95 backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/95">
        <div
          className={cn(
            'mx-auto flex h-10 w-full min-w-0 items-center',
            SITE_MAX,
            SITE_PAD_X,
          )}
        >
          <nav
            className="relative min-w-0 flex-1 overflow-hidden"
            aria-label="Alt menü — kategoriler ve etiketler"
          >
            <div
              className="pointer-events-none absolute inset-y-0 left-0 z-[1] w-3 bg-gradient-to-r from-gray-50/95 to-transparent dark:from-slate-950/95 sm:w-5"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-5 bg-gradient-to-l from-gray-50/95 to-transparent dark:from-slate-950/95 sm:w-7"
              aria-hidden
            />
            <div
              ref={scrollerRef}
              className="flex h-full min-w-0 max-w-full select-none gap-1.5 overflow-x-auto overscroll-x-contain px-0.5 [scrollbar-width:none] [-ms-overflow-style:none] sm:gap-2 [&::-webkit-scrollbar]:hidden"
            >
              {stripItems.map((item, idx) => (
                <StripPill key={item.id} item={item} active={activeIdx === idx} />
              ))}
            </div>
          </nav>
        </div>
      </div>
    </div>
  )
}
